/*! Copyright (c) Safe As Milk. All rights reserved. */
function tabClick(e) {
    if (e.keyCode === 9) {
        document.body.classList.add("js-using-tab");
        window.removeEventListener("keydown", tabClick);
    }
}

window.addEventListener("keydown", tabClick);

window.theme ??= {};

window.theme.wrapRteTables = (rootElement = document) => {
    const wrapTable = (el, wrapper) => {
        el.parentNode.insertBefore(wrapper, el);
        wrapper.appendChild(el);
    };
    rootElement.querySelectorAll(".rte table").forEach((table => {
        if (table.closest(".js-theme-utility-rte-table-wrap")) return;
        const tableWrapper = document.createElement("div");
        tableWrapper.classList.add("js-theme-utility-rte-table-wrap");
        tableWrapper.setAttribute("style", "overflow:auto;-webkit-overflow-scrolling:touch");
        wrapTable(table, tableWrapper);
    }));
};

window.theme.wrapRteTables();

const scrollEndSupported = typeof window === "undefined" ? true : "onscrollend" in window;

if (!scrollEndSupported) {
    import("scrollyfills");
}

const DEBOUNCE_DELAY = 50;
const DESKTOP_BREAKPOINT = 997;
const MOBILE_BREAKPOINT = 996;

/**
 * Debounces a function.
 * Used to prevent the function from being called too often.
 * 
 * @param {Function} f 
 * @param {Number} delay 
 * @returns {Function}
 */
const debounce = (f, delay) => {
  let timer = 0;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => f.apply(this, args), delay);
  };
};

/**
 * Infinite scroll functionality
 */

const infiniteScroll = {
  currentOffset: 0,
  currentPage: 1,
  filterParams: '',
  hasLoaded: false,
  hasPageParam: false,
  idleTimer: null,
  isLoading: false,
  itemsContainer: null,
  loadingElement: null,
  observer: null,
  pageSize: 0,
  section: null,
  startingElement: null,
  totalItems: 0,
  totalPages: 0,
  url: '',
  updateUrl: '',
  view: 'default',
};

/**
 * Gets the current page of products.
 * 
 * @returns Promise
 */

async function getPage() {
  try {
    let totalPages = infiniteScroll.totalPages;

    /**
     * Checks to see if the page has a page parameter and if the previous pages
     * have already been loaded.
     *
     * The reason is to prevent the current page from being loaded twice.
     */
    if (infiniteScroll.hasPageParam && !infiniteScroll.hasLoaded) {
      totalPages -= 1;
    }

    if (infiniteScroll.currentPage <= totalPages) {
      return fetch(`${infiniteScroll.url}&page=${infiniteScroll.currentPage}`)
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newItems = doc.querySelector('.shopify-section')?.innerHTML;

          if (
            infiniteScroll.currentPage === 1 &&
            !!(infiniteScroll.itemsContainer) &&
            !infiniteScroll.hasPageParam
          ) {
            infiniteScroll.itemsContainer.innerHTML = '';
          }

          if (infiniteScroll.hasPageParam && !infiniteScroll.hasLoaded) {
            infiniteScroll.startingElement.insertAdjacentHTML('beforebegin', newItems);
          } else {
            infiniteScroll.itemsContainer.insertAdjacentHTML('beforeend', newItems);
          }

          // Grab any skipped products and inject them

          const skippedCards = document.querySelector('.js-skipped-cards');

          if (!!(skippedCards)) {
            const script = document.createElement('script');

            script.innerHTML = skippedCards.innerHTML;
            document.body.appendChild(script);

            skippedCards.remove();
          }

          // Grab any other scripts that need to be injected

          const contentScripts = document.querySelectorAll('[data-content-script]');

          if (!!(contentScripts) && contentScripts.length > 0) {
            contentScripts.forEach((contentScript) => {
              const script = document.createElement('script');

              script.innerHTML = contentScript.innerHTML;
              document.body.appendChild(script);

              contentScript.remove();
            });
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * Initializes the infinite scroll functionality.
 */
async function initInfiniteScroll() {
  try {
    if (window.location.search.includes('page=')) {
      const urlParams = new URLSearchParams(window.location.search);
      const page = urlParams.get('page');

      if (page) {
        let tempPage = Number(page) - 1;

        if (tempPage > 0) {
          infiniteScroll.hasPageParam = true;
          window.history.scrollRestoration = 'manual';

          if (tempPage > 1 && !!(infiniteScroll.section)) {
            infiniteScroll.section.classList.add('is-loading');
          }

          for (let i = 0; i < tempPage; i += 1) {
            infiniteScroll.currentPage = i;

            await loadNextPage();
          }

          infiniteScroll.hasLoaded = true;

          infiniteScroll.currentPage = Number(page);

          /**
           * Checks the header related elements for offsetting the current
           * scroll position after all previous pages have loaded so that
           * the latest page of products is scrolled into view.
           */

          if (!!(infiniteScroll.startingElement)) {
            const headerSection = document.querySelector('header-container');
            const header = document.querySelector('.js-header');
            const announcement = document.querySelector('.shopify-section-group-header-group.js-section__announcement .section--announcement-bar');
            const utility = document.querySelector('.shopify-section-group-header-group.js-section__utility-bar');
            let scrollYCoord = 0;

            if (!!(announcement)) scrollYCoord += announcement.offsetHeight;
            if (!!(utility)) scrollYCoord += utility.offsetHeight;
            if (!!(headerSection)) scrollYCoord += headerSection.offsetHeight;

            if (!(headerSection) || !(headerSection.offsetHeight > 0)) {
              if (!!(header)) scrollYCoord += header.offsetHeight;
            }

            scrollYCoord = infiniteScroll.startingElement.getBoundingClientRect().top - scrollYCoord;

            window.scrollTo({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
              left: 0,
              top: scrollYCoord
            });

            if (!!(infiniteScroll.section)) {
              infiniteScroll.section.classList.remove('is-loading');
            }
          }
        }
      }
    }

    if (!!(infiniteScroll.loadingElement)) {
      infiniteScroll.loadingElement.textContent = 'Waiting for intersection…';

      infiniteScroll.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !infiniteScroll.isLoading) {
            loadNextPage();
          }
        });
      }, {
        rootMargin: '0px',
        threshold: 0.1
      });

      infiniteScroll.observer.observe(infiniteScroll.loadingElement);

      window.addEventListener('scroll', scrollHandler, false);

      setTimeout(() => {
        if (!!(infiniteScroll.loadingElement) && isPartiallyInViewport(infiniteScroll.loadingElement)) {
          loadNextPage();
        }
      }, 0);
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * Initializes the pagination functionality.
 */
function initPagination() {
  const gridSidebar = document.querySelector('.js-grid-sidebar');
  const gridMainContent = document.querySelector('.js-grid-main-content');

  if (!!(gridSidebar) && !!(gridMainContent)) {
    gridMainContent.style.setProperty('--sidebar-width', `${gridSidebar.getBoundingClientRect().width}px`);
  }

  const viewAllContainer = document.querySelector('.js-results-container');

  if (!!(viewAllContainer)) {
    infiniteScroll.currentOffset = Number(viewAllContainer.dataset.currentOffset);
    infiniteScroll.currentPage = Number(viewAllContainer.dataset.currentPage);
    infiniteScroll.pageSize = Number(viewAllContainer.dataset.pageSize);
    infiniteScroll.section = viewAllContainer.closest('.section');
    infiniteScroll.totalItems = Number(viewAllContainer.dataset.totalItems);
    infiniteScroll.totalPages = Number(viewAllContainer.dataset.totalPages);
    infiniteScroll.url = viewAllContainer.dataset.url;
    infiniteScroll.updateUrl = viewAllContainer.url;

    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      viewAllContainer.dataset.view = 'all';
    }

    infiniteScroll.view = viewAllContainer.dataset.view;

    let view = '';

    if (infiniteScroll.view === 'all') {
      view = 'view=all';
      infiniteScroll.updateUrl = `${location.protocol}//${location.hostname}${window.Shopify.routes.root}${infiniteScroll.url.replace('view=cards', view).slice(1)}`;
    }

    infiniteScroll.itemsContainer = viewAllContainer.querySelector('.js-results');

    if (!!(infiniteScroll.itemsContainer)) {
      infiniteScroll.startingElement = infiniteScroll.itemsContainer.firstElementChild;
    }

    if (!(infiniteScroll.currentPage === infiniteScroll.totalPages)) {
      if (!!(infiniteScroll.itemsContainer)) {
        infiniteScroll.loadingElement = infiniteScroll.itemsContainer.nextElementSibling;

        if (!(infiniteScroll.observer == null)) {
          infiniteScroll.observer.unobserve(infiniteScroll.loadingElement);

          window.removeEventListener('scroll', scrollHandler, false);
          infiniteScroll.observer = null;
        }

        if (infiniteScroll.view === 'all') {
          initInfiniteScroll();
        }
      }
    } else {
      if (!!(infiniteScroll.itemsContainer) && infiniteScroll.view === 'all') {
        infiniteScroll.loadingElement = infiniteScroll.itemsContainer.nextElementSibling;

        if (!(infiniteScroll.observer == null)) {
          infiniteScroll.observer.unobserve(infiniteScroll.loadingElement);

          window.removeEventListener('scroll', scrollHandler, false);
          infiniteScroll.observer = null;
        }

        initInfiniteScroll();
      }
    }
  }
}

/**
 * Checks if the element is partially in the viewport.
 *
 * @param {Element} element
 * @returns {boolean}
 */
function isPartiallyInViewport(element) {
  if (!!(element)) {

    const card = document.querySelector('.product-card');

    let offsetHeight = 200;

    if (!!(card)) {
      offsetHeight = card.getBoundingClientRect().height;
    }

    offsetHeight = offsetHeight * 2; // 2x offset height so that bottom should not be hit until all loaded

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isVerticallyVisible = (rect.top - offsetHeight) < viewportHeight && (rect.bottom + offsetHeight) > 0;

    return isVerticallyVisible;
  }

  return false;
}

/**
 * Loads the next page of products.
 */

async function loadNextPage() {
  if (infiniteScroll.isLoading) {
    return;
  }

  if (infiniteScroll.currentPage >= infiniteScroll.totalPages && !!(infiniteScroll.loadingElement)) {
    infiniteScroll.loadingElement.remove();

    if (infiniteScroll.observer !== null) {
      infiniteScroll.observer.unobserve(infiniteScroll.loadingElement);
    }

    return;
  }

  infiniteScroll.isLoading = true;

  if (!!(infiniteScroll.loadingElement)) {
    infiniteScroll.loadingElement.textContent = 'Loading more items…';
  }

  infiniteScroll.currentPage += 1;

  await getPage();

  //updateUrl();

  if (!!(infiniteScroll.loadingElement) && infiniteScroll.currentPage < infiniteScroll.totalPages) {
    infiniteScroll.loadingElement.textContent = 'Waiting for intersection…';
  }

  if (!!(infiniteScroll.currentPage >= infiniteScroll.totalPages) && !!(infiniteScroll.loadingElement)) {
    infiniteScroll.loadingElement.remove();

    if (infiniteScroll.observer !== null) {
      infiniteScroll.observer.unobserve(infiniteScroll.loadingElement);
    }
  }

  infiniteScroll.isLoading = false;
}

/**
 * Handles the scroll idle event.
 */
function onScrollIdle() {
  if (!!(infiniteScroll.loadingElement) && isPartiallyInViewport(infiniteScroll.loadingElement)) {
    loadNextPage();
  }
}

/**
 * Resets the idle timer.
 */
function resetIdleTimer() {
  clearTimeout(infiniteScroll.idleTimer);
  infiniteScroll.idleTimer = setTimeout(onScrollIdle, 100);
}

/**
 * Handles the scroll event.
 */
function scrollHandler() {
  if (!!(infiniteScroll.loadingElement) && isPartiallyInViewport(infiniteScroll.loadingElement)) {
    window.requestAnimationFrame(loadNextPage);
  }

  resetIdleTimer();
}

/**
 * Updates the URL.
 *
 * @param {string} title
 */
function updateUrl(title) {
  let url = new URL(infiniteScroll.updateUrl);

  url.searchParams.set('page', infiniteScroll.currentPage);

  window.history.pushState({}, title, `${url}`);
}

/**
 * Handles the resize event.
 */
window.addEventListener('resize', () => {
  requestAnimationFrame(() => {
    const gridSidebar = document.querySelector('.js-grid-sidebar');
    const gridMainContent = document.querySelector('.js-grid-main-content');

    if (!!(gridSidebar) && !!(gridMainContent)) {
      gridMainContent.style.setProperty('--sidebar-width', `${gridSidebar.getBoundingClientRect().width}px`);
    }
  });
});

document.addEventListener('DOMContentLoaded', () => {
  initPagination();
});

//# sourceMappingURL=global.js.map